import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { apiPost, apiPut, apiDelete, tryApiGet, ApiError } from "../api/client";
import { useAppData } from "../state/AppData";
import { PagingBar } from "../components/PagingBar";
import { HoverTip } from "./HoverTip";
import { AuditHistorySelect, useAuditHistory } from "./AuditHistory";
import { normalizeWindowParam } from "../lib/auditHistory";
import { displayNames, observerDisplayNames } from "../lib/auditTable";
import { ADVERT_NO_COLOUR_REASON_TOOLTIP, advertNoColourReasonLabel, type AdvertNoColourReason, AUDIT_COLOR_INFO, AUDIT_EVIDENCE_FIELD_DETECTORS, AUDIT_EVIDENCE_FIELD_LABEL, AUDIT_EVIDENCE_FIELD_TOOLTIP, AUDIT_EVIDENCE_KEY_LABEL, auditIssueTooltip, AUDIT_ROLE_ORDER, AUDIT_TRACK_LABEL, AUDIT_VERDICT_ORDER, AUDIT_VERDICT_TOOLTIP, AUDIT_WINDOW_ORDER, auditRoleLabel, auditRolesForProtocol, type AuditColor, auditColorInfo, DEFAULT_AUDIT_COLORS, EXTRA_ADVERTS_TOOLTIP, formatAdvertInterval, formatAdvertSampleBasis, formatExtraAdvertsNote, formatNeutralAdvertLine, NO_FLOOD_ADVERTS_TEXT, findingBadgeInfo, isAdvertNoColourReason, isNeutralAdvertRole, type AuditVerdict, auditVerdictInfo, isAuditColor, isAuditTrack, isAuditVerdict, detectorLabel, SHORT_ID_MATCHED_TAG, SHORT_ID_NO_CANDIDATES_TEXT, UNKNOWN_ROLE, UNRESOLVED_ROLE, type AuditWindow } from '../lib/definitions/analysis';
import { CHROME, colorFor } from '../lib/definitions/palette';
import { isProtocol, PROTOCOL_LABEL, type Protocol } from '../lib/definitions/protocols';
import { PAGE_SIZE } from '../lib/definitions/navigation';
import { formatLocalDateTime, formatShortHexId, formatSignedDuration } from "../lib/format";
import type {
  AuditDetector,
  AuditFinding,
  AuditFindingsResponse,
  AuditNodeEntry,
  AuditNodesResponse,
  AuditSavedView,
  AuditSavedViewsExport,
  AuditSavedViewsResponse,
  AuditSubjectCandidate,
} from "../api/types";

// The Behaviour tab. Rebuilt 2026-09-24 into a ranked-view surface with
// filtering, saved views, and a Nodes/Findings split; SIMPLIFIED 2026-09-25
// for two reasons:
//
// (1) The old multi-axis scoring (worst level / airtime tier / proven facts
//     / airtime impact / counts) was confusing and unhelpful — a node's
//     status should be a plain timer plus a red/yellow/green mark, not an
//     abstract "against policy" score; airtime tier, proven facts, and
//     airtime impact are not useful as standalone metrics. It is GONE,
//     replaced by a traffic light (Node · Role ·
//     Advert interval · Issues — see NodeRow/AdvertIntervalCell/IssueDots
//     below) and a Red/Yellow/Green/No-colour filter (see AUDIT_COLOR_*).
// (2) The filtering was too complex to follow at a glance — every OTHER
//     filter control (Role, Heard by, How sure,
//     first/last seen, numeric evidence ranges, the Tags placeholder) now
//     lives behind one collapsed "More filters" disclosure; the always-
//     visible row is just Search + colour chips + a single Check dropdown
//     + a window dropdown. See the render below for the exact layout.
//
// Every OTHER audit tab (Reachability, Regions, Config) is untouched —
// this component only ever renders inside AuditPage.tsx's `tab ===
// "behaviour"` branch.
//
// Protocol-scoped (2026-09-25, audit protocol-split task): `protocol` is
// a controlled prop from AuditPage's own top-level MeshCore|Meshtastic
// switch, not a dropdown this tab owns itself (see the sync effect on
// `protocol` below). The Check filter, the Role filter, and the numeric-
// evidence fields under "More filters" all list only what's relevant to
// the active protocol (`protocolDetectors`/`roleOrder`/
// `relevantEvidenceFields` below).

// The numeric evidence fields offered under "More filters" as min/max
// bounds — labels and detector ownership are both registered once in
// lib/definitions.ts (AUDIT_EVIDENCE_FIELD_LABEL/AUDIT_EVIDENCE_FIELD_
// DETECTORS), not hand-written here. `ev_<field>_min`/`ev_<field>_max` is
// the exact backend query param shape (routes/audit.py's own
// `_evidence_bounds_from_request`).
const ALL_EVIDENCE_FIELDS = Object.keys(AUDIT_EVIDENCE_FIELD_LABEL);

// The three chips shown in the always-visible row: Red/Yellow/Green, with
// Red+Yellow on by default; a "No colour" chip for nodes without a colour
// lives inside More filters instead, rather than cluttering this row.
// "none" lives under More filters — see NO_COLOUR_CHIP below.
const PRIMARY_COLOR_CHIPS: AuditColor[] = ["red", "yellow", "green"];
const NO_COLOUR_CHIP: AuditColor = "none";

type SubView = "nodes" | "findings";

interface FilterState {
  sub: SubView;
  window: AuditWindow;
  view: string | null;
  color: AuditColor[];
  // Single-select in the UI (a single Check dropdown) — kept as
  // a 0-or-1-element array under the hood so the existing multi-value
  // query-param/saved-view plumbing (?detector=x&detector=y, same shape
  // the backend already accepts) needs no format change.
  detector: string[];
  verdict: AuditVerdict[];
  protocol: string;
  q: string;
  native_id: string;
  observer: string;
  first_seen_from: string; // plain "YYYY-MM-DD", converted to full ISO at query-build time
  last_seen_to: string;
  role: string[];
  evidence: Record<string, string>; // "ev_<field>_min" / "ev_<field>_max" -> raw string value
}

const OWNED_URL_KEYS = ["sub", "window", "view", "color", "detector", "verdict", "protocol", "q", "native_id", "observer", "first_seen_from", "last_seen_to", "role", "tag"];

function currentHashParts(): { pathPart: string; params: URLSearchParams } {
  const raw = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  const qIdx = raw.indexOf("?");
  const pathPart = qIdx >= 0 ? raw.slice(0, qIdx) : raw;
  const params = new URLSearchParams(qIdx >= 0 ? raw.slice(qIdx + 1) : "");
  return { pathPart, params };
}

function isDefaultColorSet(color: AuditColor[]): boolean {
  return color.length === DEFAULT_AUDIT_COLORS.length && DEFAULT_AUDIT_COLORS.every((c) => color.includes(c));
}

function parseFilterState(params: URLSearchParams): FilterState {
  const subRaw = params.get("sub");
  const sub: SubView = subRaw === "findings" ? "findings" : "nodes";
  const windowRaw = params.get("window");
  const windowParam = normalizeWindowParam(windowRaw); // the retired 30d reads as 7d
  const windowKey: AuditWindow = windowParam && (AUDIT_WINDOW_ORDER as string[]).includes(windowParam) ? (windowParam as AuditWindow) : "24h";

  let color: AuditColor[];
  if (params.has("color")) {
    const raw = params.getAll("color");
    color = raw.includes("__none__") ? [] : raw.filter(isAuditColor);
  } else {
    color = DEFAULT_AUDIT_COLORS;
  }

  const evidence: Record<string, string> = {};
  for (const [k, v] of params.entries()) {
    if (k.startsWith("ev_")) evidence[k] = v;
  }

  return {
    sub,
    window: windowKey,
    view: params.get("view"),
    color,
    detector: params.getAll("detector"),
    verdict: params.getAll("verdict").filter(isAuditVerdict),
    protocol: params.get("protocol") ?? "",
    q: params.get("q") ?? "",
    native_id: params.get("native_id") ?? "",
    observer: params.get("observer") ?? "",
    first_seen_from: params.get("first_seen_from") ?? "",
    last_seen_to: params.get("last_seen_to") ?? "",
    role: params.getAll("role").filter((r) => AUDIT_ROLE_ORDER.includes(r)),
    evidence,
  };
}

function writeFilterState(state: FilterState): void {
  const { pathPart, params } = currentHashParts();
  for (const k of OWNED_URL_KEYS) params.delete(k);
  for (const k of Array.from(params.keys())) if (k.startsWith("ev_")) params.delete(k);

  if (state.sub !== "nodes") params.set("sub", state.sub);
  if (state.window !== "24h") params.set("window", state.window);
  if (state.view) params.set("view", state.view);
  if (state.color.length === 0) {
    params.set("color", "__none__");
  } else if (!isDefaultColorSet(state.color)) {
    for (const c of state.color) params.append("color", c);
  }
  for (const d of state.detector) params.append("detector", d);
  for (const v of state.verdict) params.append("verdict", v);
  if (state.protocol) params.set("protocol", state.protocol);
  if (state.q) params.set("q", state.q);
  if (state.native_id) params.set("native_id", state.native_id);
  if (state.observer) params.set("observer", state.observer);
  if (state.first_seen_from) params.set("first_seen_from", state.first_seen_from);
  if (state.last_seen_to) params.set("last_seen_to", state.last_seen_to);
  for (const r of state.role) params.append("role", r);
  for (const [k, v] of Object.entries(state.evidence)) if (v) params.set(k, v);

  const qs = params.toString();
  const newHash = `#${pathPart}${qs ? `?${qs}` : ""}`;
  if (newHash !== window.location.hash) window.history.replaceState(null, "", newHash);
}

function dateToIsoStart(d: string): string {
  return `${d}T00:00:00.000000Z`;
}
function dateToIsoEnd(d: string): string {
  return `${d}T23:59:59.999999Z`;
}

// The query params GET /api/audit/findings and GET /api/audit/nodes both
// accept, built from filter state. `color` is only meaningful to GET
// /api/audit/nodes (routes/audit.py) — GET /api/audit/findings silently
// ignores an unrecognized query param, same as it always has for any
// extra param, so sharing one query-param builder across both fetches
// (see usePagedQuery below) stays safe. `limit`/`cursor` are added by the
// caller (usePagedQuery below).
function buildQueryParams(state: FilterState): URLSearchParams {
  const p = new URLSearchParams();
  p.set("window", state.window);
  for (const c of state.color) p.append("color", c);
  for (const d of state.detector) p.append("detector", d);
  for (const v of state.verdict) p.append("verdict", v);
  if (state.protocol) p.set("protocol", state.protocol);
  if (state.q) p.set("q", state.q);
  if (state.native_id) p.set("native_id", state.native_id);
  if (state.observer) p.set("observer", state.observer);
  if (state.first_seen_from) p.set("first_seen_from", dateToIsoStart(state.first_seen_from));
  if (state.last_seen_to) p.set("last_seen_to", dateToIsoEnd(state.last_seen_to));
  for (const r of state.role) p.append("role", r);
  for (const [k, v] of Object.entries(state.evidence)) if (v) p.set(k, v);
  return p;
}

// What gets POSTed/PUT for a saved view -- `color` is always present
// (explicit, even empty/default) since it's the primary axis views are
// built around; everything else only appears when actually set. Migration
// 0035 (2026-09-25) rewrote the three seeded builtins from the old `level`
// vocabulary straight to this one, so every saved view a caller can select
// already speaks `color` -- applyViewFilters below still drops any value
// isAuditColor() doesn't recognize rather than crashing, for a hand-edited
// import or a future vocabulary change.
// `protocol` is deliberately NOT included here (unlike every other field
// in `state`) -- it's a controlled prop now, not something a saved view's
// filters comparison should key on; saveViewAsNew/updateActiveView below
// add the real active `protocol` onto this payload only at the point a
// view is actually written, so a saved view still records which side it
// was saved on (task's own requirement) without the equality checks below
// (`matchedView`, `filtersDifferFromActiveView`) spuriously mismatching a
// protocol-less legacy view (the three seeded builtins) against the
// current, always-set protocol.
function filtersToViewPayload(state: FilterState): Record<string, unknown> {
  const payload: Record<string, unknown> = { color: state.color };
  if (state.detector.length) payload.detector = state.detector;
  if (state.verdict.length) payload.verdict = state.verdict;
  if (state.q) payload.q = state.q;
  if (state.native_id) payload.native_id = state.native_id;
  if (state.observer) payload.observer = state.observer;
  if (state.first_seen_from) payload.first_seen_from = dateToIsoStart(state.first_seen_from);
  if (state.last_seen_to) payload.last_seen_to = dateToIsoEnd(state.last_seen_to);
  if (state.role.length) payload.role = state.role;
  for (const [k, v] of Object.entries(state.evidence)) if (v) payload[k] = v;
  return payload;
}

function canonicalize(obj: Record<string, unknown>): string {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(obj).sort()) {
    const v = obj[key];
    out[key] = Array.isArray(v) ? [...v].sort() : v;
  }
  return JSON.stringify(out);
}

// Applies a saved view's own filters onto the CURRENT filter state --
// only the fields a view actually carries are changed; sub/window (not
// part of a view) are left alone. `tag` (not modeled yet, still a
// disabled "Coming soon" control) is ignored here for rendering purposes
// but preserved in `rawFilters` (see BehaviourTab's own state) so
// re-saving the same view doesn't silently drop it. `role` is parsed like
// `detector`/`verdict`, any value not in AUDIT_ROLE_ORDER is dropped.
function applyViewFilters(current: FilterState, filters: Record<string, unknown>): FilterState {
  const asStringArray = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
  const evidence: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (k.startsWith("ev_") && (typeof v === "string" || typeof v === "number")) evidence[k] = String(v);
  }
  return {
    ...current,
    color: asStringArray(filters.color).filter(isAuditColor),
    detector: asStringArray(filters.detector),
    verdict: asStringArray(filters.verdict).filter(isAuditVerdict),
    protocol: typeof filters.protocol === "string" ? filters.protocol : "",
    q: typeof filters.q === "string" ? filters.q : "",
    native_id: typeof filters.native_id === "string" ? filters.native_id : "",
    observer: typeof filters.observer === "string" ? filters.observer : "",
    first_seen_from: typeof filters.first_seen_from === "string" ? filters.first_seen_from.slice(0, 10) : "",
    last_seen_to: typeof filters.last_seen_to === "string" ? filters.last_seen_to.slice(0, 10) : "",
    role: asStringArray(filters.role).filter((r) => AUDIT_ROLE_ORDER.includes(r)),
    evidence,
  };
}

// --- Paged fetch over an arbitrary URLSearchParams (repeated keys like
// color/detector/verdict make the Record<string,string>-based
// usePagedFetch unusable here) -- same Prev/Next-via-cursor-stack
// discipline as api/usePagedFetch.ts.
// `enabled: false` skips the actual network fetch entirely (hook order
// must stay stable across renders, so both the Nodes and Findings
// sub-view's own usePagedQuery call always run -- only ONE of them is
// ever `enabled`, matching which sub-view is currently showing, so
// switching sub-views never fires a redundant fetch for data nobody is
// looking at and a stale/never-requested `data` for the inactive one is
// never mistaken for "loaded but empty" (it stays `null`, the same
// "still loading" state a fresh mount would show, until it becomes
// active).
function usePagedQuery<T extends { next_cursor?: string | null }>(path: string, queryParams: URLSearchParams, limit: number, enabled: boolean) {
  const paramsKey = queryParams.toString();
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [data, setData] = useState<T | null | "unavailable">(null);

  useEffect(() => {
    setCursorStack([null]);
    setPageIndex(0);
  }, [path, paramsKey]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setData(null);
    const cursor = cursorStack[pageIndex] ?? null;
    const search = new URLSearchParams(paramsKey);
    search.set("limit", String(limit));
    if (cursor) search.set("cursor", cursor);
    tryApiGet<T>(`${path}?${search.toString()}`)
      .then((res) => !cancelled && setData(res ?? "unavailable"))
      .catch(() => !cancelled && setData("unavailable"));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, paramsKey, pageIndex, limit, enabled]);

  function next() {
    if (!data || data === "unavailable") return;
    const cursor = data.next_cursor;
    if (!cursor) return;
    setCursorStack((stack) => (pageIndex === stack.length - 1 ? [...stack, cursor] : stack));
    setPageIndex((i) => i + 1);
  }
  function prev() {
    setPageIndex((i) => Math.max(0, i - 1));
  }

  return { data, page: pageIndex + 1, hasPrev: pageIndex > 0, hasNext: !!(data && data !== "unavailable" && data.next_cursor), next, prev };
}

// `track` is optional context only (a "fact" level is shared by both the
// airtime and proven_fact tracks) — shown as a title tooltip on the
// ambiguous case so hovering tells you which axis this "fact" actually
// sits on, without cluttering the badge itself with a second label. Used
// only by the Findings sub-view now — the Nodes list's own severity
// signal is the colour traffic light (AdvertIntervalCell/IssueDots
// below), not a finding's `level`.
function LevelBadge({ level, track, detector, verdict }: {
  level: string | null | undefined; track?: string | null; detector?: string | null; verdict?: string | null;
}) {
  const { effectiveTheme } = useAppData();
  const info = findingBadgeInfo(level, detector, verdict);
  const tip = level === "fact" && isAuditTrack(track) && info.label === "Fact" ? `${AUDIT_TRACK_LABEL[track]} track` : undefined;
  const badge = (
    <span className="badge" style={{ background: colorFor(info.color, effectiveTheme) }}>
      {info.label}
    </span>
  );
  return tip ? <HoverTip text={tip}>{badge}</HoverTip> : badge;
}

// Plain-short-label rendering for a finding's own evidence dict
// (2026-09-25 D1/D2/D4/D5 check-fixes rebuild) — label comes from
// lib/definitions.ts's AUDIT_EVIDENCE_KEY_LABEL (falling back to the raw
// key for anything not registered there, never a crash), value formatting
// keeps arrays/objects short rather than a raw JSON.stringify dump. D2's
// own reset_events/clock_backward_fact_events arrays get a dedicated
// per-event rendering (D2EventList below, 2026-09-25 readability task) —
// jump/elapsed seconds as human durations, timestamps as local
// date-times — everything else falls back to this generic formatter.
function formatEvidenceValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) {
    if (v.length === 0) return "none";
    if (v.every((x) => x === null || typeof x !== "object")) return v.map(String).join(", ");
    return `${v.length} event(s)`;
  }
  if (typeof v === "object") {
    return Object.entries(v as Record<string, unknown>)
      .map(([k, vv]) => `${k}: ${typeof vv === "number" ? Math.round(vv * 100) / 100 : String(vv)}`)
      .join(", ");
  }
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(2);
  return String(v);
}

interface D2Event {
  before_observed_at?: string;
  after_observed_at?: string;
  jump_seconds?: number;
  elapsed_seconds?: number;
}

function isD2EventArray(v: unknown): v is D2Event[] {
  return Array.isArray(v) && v.length > 0 && v.every((x) => x !== null && typeof x === "object" && "jump_seconds" in (x as object));
}

// D2's own restart-evidence readability (2026-09-25): D2 jump/elapsed
// seconds render as human durations ("2.4 years back", "13 min later");
// dates as local date-times. `jump_seconds` is always
// negative by construction (a backward clock jump — d2_boot_loop.py's own
// docstring), so formatSignedDuration always reads "... back"; elapsed_
// seconds is always positive, so it always reads "... later" — both
// examples above come straight out of this one function, no special
// casing needed per sign.
function D2EventList({ events }: { events: D2Event[] }) {
  return (
    <ul className="coverage-disagreement-values">
      {events.map((e, i) => (
        <li key={i}>
          clock jumped {formatSignedDuration(e.jump_seconds)} ({formatSignedDuration(e.elapsed_seconds)} elapsed) —{" "}
          {formatLocalDateTime(e.before_observed_at)} → {formatLocalDateTime(e.after_observed_at)}
        </li>
      ))}
    </ul>
  );
}

function EvidenceList({ evidence }: { evidence: Record<string, unknown> }) {
  const entries = Object.entries(evidence || {}).filter(([, v]) => v !== null && v !== undefined);
  if (entries.length === 0) return <span className="settings-field-desc">No evidence recorded.</span>;
  return (
    <ul className="coverage-disagreement-values">
      {entries.map(([k, v]) => (
        <li key={k}>
          <strong>{AUDIT_EVIDENCE_KEY_LABEL[k] || AUDIT_EVIDENCE_FIELD_LABEL[k] || k}</strong>:{" "}
          {isD2EventArray(v) ? <D2EventList events={v} /> : formatEvidenceValue(v)}
        </li>
      ))}
    </ul>
  );
}

function FindingsSubTable({
  findings,
  showEmptyMessage,
  detectors,
}: {
  findings: AuditFinding[];
  showEmptyMessage: string;
  detectors: AuditDetector[];
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { observers: allObservers } = useAppData();
  const names = useMemo(() => observerDisplayNames(allObservers), [allObservers]);
  const meaningById = useMemo(() => new Map(detectors.map((d) => [d.id, d.meaning])), [detectors]);

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="node-list-table-wrap">
      <table className="node-list-table">
        <thead>
          <tr>
            <th>Level</th>
            <th>Node</th>
            <th>Check</th>
            <th>How sure</th>
            <th>Why</th>
            <th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {findings.map((f) => {
            const subjectLabel = f.subject.label || f.subject.native_id;
            const isOpen = expanded.has(f.id);
            // Short-ID resolution (2026-09-25) -- an unresolved subject's
            // native_id is still a raw hash prefix, not a real node id, so
            // linking to "its" node page would 404/dead-end; render plain
            // text instead, same rule NodeRow applies to the Nodes list.
            const isUnresolved = f.subject.attribution === "unresolved";
            const isMatchedByShortId = f.subject.attribution === "matched_by_short_id";
            return (
              <Fragment key={f.id}>
                <tr className="node-list-row" onClick={() => toggle(f.id)}>
                  <td>
                    <LevelBadge level={f.level} track={f.track} detector={f.detector} verdict={f.verdict} />
                  </td>
                  <td>
                    {isUnresolved ? (
                      <span className="node-name-unresolved">{subjectLabel}</span>
                    ) : (
                      <>
                        <a
                          href={`#/nodes/${encodeURIComponent(f.subject.native_id)}`}
                          className="node-name-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {subjectLabel}
                        </a>
                        {isMatchedByShortId && <span className="badge-muted">{SHORT_ID_MATCHED_TAG}</span>}
                      </>
                    )}{" "}
                    <span className="audit-subject-protocol">({isProtocol(f.subject.protocol) ? PROTOCOL_LABEL[f.subject.protocol] : f.subject.protocol})</span>
                  </td>
                  <td>
                    {meaningById.get(f.detector)
                      ? <HoverTip text={meaningById.get(f.detector) as string}>{detectorLabel(f.detector, f.detector_label)}</HoverTip>
                      : detectorLabel(f.detector, f.detector_label)}
                  </td>
                  <td>
                    <span className="badge-outline" style={{ color: colorFor(auditVerdictInfo(f.verdict).color, "light") }}>
                      {auditVerdictInfo(f.verdict).label}
                    </span>
                  </td>
                  <td>{f.level_reason ? displayNames(f.level_reason, names) : "—"}</td>
                  <td>{formatLocalDateTime(f.last_seen)}</td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={6} style={{ background: "var(--page)" }}>
                      <div className="node-detail-note">{displayNames(f.summary, names)}</div>
                      <EvidenceList evidence={f.evidence} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {findings.length === 0 && (
            <tr>
              <td colSpan={6} className="empty-state">
                {showEmptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// Short-ID resolution (2026-09-25): what an unresolved subject's expanded
// row shows instead of a single linked node — every node whose full
// native_id starts with the prefix that couldn't be resolved uniquely, so
// a person can pick the right one by eye. Each candidate links to its own
// node page exactly like a resolved node name would.
function UnresolvedCandidateList({ candidates }: { candidates: AuditSubjectCandidate[] | null | undefined }) {
  if (!candidates || candidates.length === 0) {
    return <div className="node-detail-note">{SHORT_ID_NO_CANDIDATES_TEXT}</div>;
  }
  return (
    <div className="node-detail-note">
      <div>Candidates matching this ID:</div>
      <ul>
        {candidates.map((c) => (
          <li key={c.native_id}>
            <a href={`#/nodes/${encodeURIComponent(c.native_id)}`} className="node-name-link">
              {c.label || c.native_id}
            </a>
            {c.role && <span className="settings-field-desc"> — {auditRoleLabel(c.role)}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// D1's own traffic light — just the timer, marked red/yellow/green,
// with the raw number always shown. States:
//   - a real colour (red/yellow/green): a filled, coloured badge, plus a
//     small grey "from N gaps over X days" sample-basis line underneath
//     (2026-09-26 check-fixes batch 1, fix 3).
//   - a real interval but NO colour (slower than the neutral ceiling): the
//     plain number, no badge at all, same sample-basis line underneath —
//     "no colour" read literally, not a 4th grey hue.
//   - a NEUTRAL_ADVERT_ROLES node (companion/observer — no confirmed
//     flood-advert timer at all, fix 1 "COMPANIONS NEUTRAL"): a plain grey
//     "N flood adverts from the app, last gap X" line, never a colour, an
//     interval reading, or a sample-basis line (there's no rate to sample).
//   - too few samples: "not enough adverts yet (n of N)", muted grey text
//     (fix 2 — names N, D1's own derived minimum, rather than leaving the
//     reader to guess what n is short of). No sample-basis line here too —
//     the "(n of N)" already says the same thing.
//   - no D1 data at all for this node: an em dash.
// A REPEATER/ROOM_SERVER with `advert_extra_adverts > 0` gets an
// additional small grey "+N manual adverts" note under any of the above
// (fix 2, "EXTRA ADVERTS") — a real, distinct advert the CLI `advert`
// command (locally or by remote admin) sent between timer beats, never
// folded into the interval/colour math itself.
// 1d ("NO-COLOUR REASON PILL SHAPE", 2026-09-26 check-fixes batch 1): a
// no-colour reason (not enough adverts yet / no timer / no flood adverts
// heard / over the neutral ceiling) renders as a grey OUTLINED pill — the
// same `.badge-outline` shape (border: 1px solid currentColor, same
// padding/radius) the coloured D1 pills and the "How sure" verdict chips
// already use — rather than plain unshaped text, so every state of this
// cell reads as "a pill", just filled (a real colour) or outlined (no
// colour), never a mix of pill and plain prose.
function NoColourPill({ children, title }: { children: ReactNode; title?: string }) {
  const { effectiveTheme } = useAppData();
  return (
    <span className="badge-outline" style={{ color: colorFor(CHROME.inkMuted, effectiveTheme) }} title={title}>
      {children}
    </span>
  );
}

function AdvertIntervalCell({ node, lookbackDays, neutralCeilingHours }: { node: AuditNodeEntry; lookbackDays?: number; neutralCeilingHours?: number }) {
  const { effectiveTheme } = useAppData();
  const extraAdvertsNote = node.advert_extra_adverts ? (
    <div className="settings-field-desc" title={EXTRA_ADVERTS_TOOLTIP}>
      {formatExtraAdvertsNote(node.advert_extra_adverts)}
    </div>
  ) : null;

  if (isNeutralAdvertRole(node.role) && node.advert_no_colour_reason === "no_timer") {
    return (
      <>
        <span
          className="settings-field-desc"
          title={ADVERT_NO_COLOUR_REASON_TOOLTIP.no_timer}
        >
          {formatNeutralAdvertLine(node.advert_event_count ?? 0, node.advert_last_gap_hours)}
        </span>
        {extraAdvertsNote}
      </>
    );
  }

  if (node.advert_interval_hours != null) {
    const info = auditColorInfo(node.advert_color);
    const sampleBasis =
      node.advert_sample_count != null && lookbackDays != null ? (
        <div className="settings-field-desc">{formatAdvertSampleBasis(node.advert_sample_count, lookbackDays)}</div>
      ) : null;
    const reasonNote =
      !info.color && isAdvertNoColourReason(node.advert_no_colour_reason) ? (
        <div>
          <NoColourPill title={ADVERT_NO_COLOUR_REASON_TOOLTIP[node.advert_no_colour_reason]}>
            {advertNoColourReasonLabel(node.advert_no_colour_reason, node.role, neutralCeilingHours)}
          </NoColourPill>
        </div>
      ) : null;
    return (
      <>
        {info.color ? (
          <span className="badge" style={{ background: colorFor(info.color, effectiveTheme) }}>
            {formatAdvertInterval(node.advert_interval_hours)}
          </span>
        ) : (
          <span>{formatAdvertInterval(node.advert_interval_hours)}</span>
        )}
        {sampleBasis}
        {reasonNote}
        {extraAdvertsNote}
      </>
    );
  }
  if (node.advert_sample_count != null && node.advert_min_required != null) {
    return (
      <>
        <NoColourPill title={isAdvertNoColourReason(node.advert_no_colour_reason) ? ADVERT_NO_COLOUR_REASON_TOOLTIP[node.advert_no_colour_reason] : undefined}>
          not enough adverts yet ({node.advert_sample_count} of {node.advert_min_required})
        </NoColourPill>
        {extraAdvertsNote}
      </>
    );
  }
  // 2026-09-26 check-fixes batch 2, fix 3 ("NO-ADVERTS COMPANION shows
  // dash"): a `no_adverts` node has zero D1 advert data at all -- that's
  // a fact ("no flood adverts heard"), not an uninformative dash. Any
  // OTHER reason reaching here (or none at all, e.g. a non-MeshCore
  // protocol D1 never runs against) keeps the plain em dash.
  if (node.advert_no_colour_reason === "no_adverts") {
    return (
      <>
        <NoColourPill title={ADVERT_NO_COLOUR_REASON_TOOLTIP.no_adverts}>{NO_FLOOD_ADVERTS_TEXT}</NoColourPill>
        {extraAdvertsNote}
      </>
    );
  }
  return (
    <>
      <span className="settings-field-desc">—</span>
      {extraAdvertsNote}
    </>
  );
}

// The OTHER checks that flagged this node — Issues shows the checks that
// flagged it, plain names, each with its own colour dot where the check
// has one. `issue.color` is only ever set today when the issue IS a D1
// violation (d1_advert_rate's own evidence.color) — every other detector's
// issue renders as a plain, undotted name. Each chip carries a one-line
// explanation tooltip (2026-09-26 check-fixes batch 1, fix 6) — this
// task's own richer wording for d5 (AUDIT_ISSUE_TOOLTIP), falling back
// to the backend's own detector `meaning` (GET /api/audit/detectors) for
// every other check, never a raw id with no explanation at all.
function IssueDots({
  issues,
  detectors,
  clockColorSettings,
}: {
  issues: AuditNodeEntry["issues"];
  detectors: AuditDetector[];
  clockColorSettings?: { yellow_from_s: number; red_from_s: number };
}) {
  const { effectiveTheme } = useAppData();
  if (!issues || issues.length === 0) return <span className="settings-field-desc">—</span>;
  return (
    <span className="audit-issue-list">
      {issues.map((issue) => {
        const info = issue.color && isAuditColor(issue.color) ? auditColorInfo(issue.color) : null;
        const meaning = auditIssueTooltip(issue.detector, clockColorSettings) ?? detectors.find((d) => d.id === issue.detector)?.meaning ?? undefined;
        return (
          <span key={issue.detector} className="audit-issue-chip" title={meaning}>
            {info?.color && (
              <span
                className="legend-swatch"
                style={{ background: colorFor(info.color, effectiveTheme), display: "inline-block", marginRight: 4 }}
              />
            )}
            {detectorLabel(issue.detector, issue.label)}
          </span>
        );
      })}
    </span>
  );
}

function NodeRow({
  node,
  queryParams,
  detectors,
  advertLookbackDays,
  advertNeutralCeilingHours,
  clockColorSettings,
}: {
  node: AuditNodeEntry;
  queryParams: URLSearchParams;
  detectors: AuditDetector[];
  advertLookbackDays?: number;
  advertNeutralCeilingHours?: number;
  clockColorSettings?: { yellow_from_s: number; red_from_s: number };
}) {
  const [expanded, setExpanded] = useState(false);
  const [findings, setFindings] = useState<AuditFinding[] | null | "unavailable">(null);
  const queryParamsKey = queryParams.toString();

  // Re-fetch whenever the parent's filters change while expanded (not just
  // on first expand) -- an already-open row must not keep showing findings
  // fetched under a stale window/filter combination.
  useEffect(() => {
    setFindings(null);
  }, [queryParamsKey, node.native_id]);

  useEffect(() => {
    if (!expanded || findings !== null) return;
    let cancelled = false;
    const search = new URLSearchParams(queryParamsKey);
    search.set("native_id", node.native_id);
    search.set("limit", String(PAGE_SIZE));
    tryApiGet<AuditFindingsResponse>(`/api/audit/findings?${search.toString()}`)
      .then((res) => !cancelled && setFindings(res ? res.findings : "unavailable"))
      .catch(() => !cancelled && setFindings("unavailable"));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, node.native_id, queryParamsKey, findings]);

  // Short-ID resolution (2026-09-25) — an "unresolved" subject has no real
  // node to link to (its native_id is still the raw hash prefix), so its
  // name renders as plain text; a "matched_by_short_id" subject IS a real,
  // linkable node, just flagged with a small muted tag so its provenance
  // stays honest rather than looking like an ordinary identified node.
  const isUnresolved = node.attribution === "unresolved";
  const isMatchedByShortId = node.attribution === "matched_by_short_id";

  return (
    <>
      <tr className="node-list-row" onClick={() => setExpanded((e) => !e)}>
        <td className="behaviour-node-cell">
          {isUnresolved ? (
            <span className="node-name-unresolved">{node.label || node.native_id}</span>
          ) : (
            <>
              <a
                href={`#/nodes/${encodeURIComponent(node.native_id)}`}
                className="node-name-link"
                onClick={(e) => e.stopPropagation()}
              >
                {node.label || node.native_id}
              </a>
              {isMatchedByShortId && <span className="badge-muted">{SHORT_ID_MATCHED_TAG}</span>}
            </>
          )}
          {/* Short hex ID under the name (2026-09-26 check-fixes batch 1,
              fix 6) — the node's own public-key prefix, same truncation
              this app already uses elsewhere for an unnamed node/observer
              (lib/format.ts's formatShortHexId). Shown even when the name
              IS the raw native_id (nothing to add there) only when a real
              label exists, so the id isn't printed twice. */}
          {!isUnresolved && node.label && (
            <div className="behaviour-node-short-id settings-field-desc">{formatShortHexId(node.native_id)}</div>
          )}
        </td>
        <td data-label="Role">{node.role ? auditRoleLabel(node.role) : "—"}</td>
        <td data-label="Advert interval">
          <AdvertIntervalCell node={node} lookbackDays={advertLookbackDays} neutralCeilingHours={advertNeutralCeilingHours} />
        </td>
        <td data-label="Issues">
          <IssueDots issues={node.issues} detectors={detectors} clockColorSettings={clockColorSettings} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={4} className="behaviour-node-detail-cell" style={{ background: "var(--page)" }}>
            {isUnresolved && <UnresolvedCandidateList candidates={node.candidates} />}
            {findings === null && <div className="node-detail-note">Loading findings…</div>}
            {findings === "unavailable" && <div className="node-detail-note">Could not load findings for this node.</div>}
            {Array.isArray(findings) && (
              <FindingsSubTable findings={findings} showEmptyMessage="No findings for this node with the current filters." detectors={detectors} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// `protocol` is a CONTROLLED prop, driven by AuditPage's own top-level
// MeshCore|Meshtastic switch (2026-09-25 protocol split) — this tab no
// longer offers its own protocol dropdown. `filters.protocol` still
// exists in FilterState (below) purely so it keeps flowing into
// buildQueryParams/filtersToViewPayload/the URL exactly as it always has
// — a saved view still carries `protocol` in its filters, per this
// task's own requirement — it's just synced FROM this prop (see the
// effect below) rather than settable by the user from inside this tab.
export function BehaviourTab({ protocol }: { protocol: Protocol }) {
  const [filters, setFilters] = useState<FilterState>(() => parseFilterState(currentHashParts().params));
  const [qDraft, setQDraft] = useState(filters.q);
  const [views, setViews] = useState<AuditSavedView[] | "unavailable" | null>(null);
  const [detectors, setDetectors] = useState<AuditDetector[]>([]);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { observers, effectiveTheme } = useAppData();

  useEffect(() => {
    setFilters((f) => (f.protocol === protocol ? f : { ...f, protocol }));
  }, [protocol]);

  // Only the detectors declared for the active protocol — the Check
  // dropdown, the crashed-detector error banner's own labels, and the
  // numeric-evidence fields under "More filters" are all scoped to this
  // list, never the full 8-detector roster.
  const protocolDetectors = useMemo(() => detectors.filter((d) => d.protocols.includes(protocol)), [detectors, protocol]);
  // "Unresolved ID" (2026-09-25 short-id resolution) is its own role-filter
  // value, separate from "Unknown", and only ever real on the MeshCore side
  // — a Meshtastic finding's subject is never a hash-prefix.
  const roleOrder = useMemo(
    () => (protocol === "meshcore" ? [...auditRolesForProtocol(protocol), UNKNOWN_ROLE, UNRESOLVED_ROLE] : [...auditRolesForProtocol(protocol), UNKNOWN_ROLE]),
    [protocol],
  );
  // 2026-09-26 check-fixes third pass, fix 4 ("OTHER CHECKS:" LABEL): the
  // window menu below narrows every check EXCEPT the Advert-interval
  // column (D1 always uses its own fixed look-back — see the column
  // header's own note). The tooltip names those OTHER checks by their real
  // UI labels, read from the SAME backend detector registry the "All
  // checks" dropdown below already uses, rather than a hard-coded
  // "D2, D3, D4, D5, D7, D8..." that silently goes stale the next time a
  // detector is added or renamed.
  const otherChecksNames = useMemo(
    () => protocolDetectors.filter((d) => d.id !== "d1_advert_rate").map((d) => detectorLabel(d.id, d.label)),
    [protocolDetectors],
  );
  const otherChecksTooltip = `This time window narrows every OTHER check below${
    otherChecksNames.length > 0 ? ` (${otherChecksNames.join(", ")})` : ""
  }. The Advert interval column never uses it — that column always measures over its own fixed look-back (see the note under the column header).`;
  const relevantEvidenceFields = useMemo(
    () =>
      ALL_EVIDENCE_FIELDS.filter((field) =>
        (AUDIT_EVIDENCE_FIELD_DETECTORS[field] || []).some((id) => protocolDetectors.some((d) => d.id === id)),
      ),
    [protocolDetectors],
  );

  // Active view's own last-known-saved filters (for the "differs" check
  // that shows/hides Update view) -- kept separate from `filters` itself
  // (which changes as the user edits) and re-set only when a view is
  // explicitly selected/saved/updated.
  const [activeViewFilters, setActiveViewFilters] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    writeFilterState(filters);
  }, [filters]);

  // Debounced search box: `qDraft` updates the input instantly; `filters.q`
  // (which drives the URL + the actual fetch) only updates 400ms after
  // typing stops -- the task's own explicit requirement ("debounce the
  // search box").
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.q === qDraft ? f : { ...f, q: qDraft }));
    }, 400);
    return () => clearTimeout(t);
  }, [qDraft]);

  useEffect(() => {
    let cancelled = false;
    tryApiGet<AuditSavedViewsResponse>("/api/audit/views")
      .then((res) => !cancelled && setViews(res ? res.views : "unavailable"))
      .catch(() => !cancelled && setViews("unavailable"));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    tryApiGet<{ detectors: AuditDetector[] }>("/api/audit/detectors")
      .then((res) => !cancelled && setDetectors(res?.detectors ?? []))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // While a History snapshot is chosen the read is that snapshot's 7-day result.
  const { takenAt } = useAuditHistory();
  const queryParams = useMemo(() => {
    const p = buildQueryParams(filters);
    if (takenAt) {
      p.set("window", "7d");
      p.set("taken_at", takenAt);
    }
    return p;
  }, [filters, takenAt]);
  // Findings sub-view only, worst-first (backend/amalgamesh/audit/
  // filters.py's sort_worst_first: level, then verdict, then last_seen
  // desc) — a query-params clone so the Nodes fetch and each expanded
  // NodeRow's own per-node findings fetch (which shares `queryParams`)
  // keep their existing, unsorted behaviour untouched.
  const findingsQueryParams = useMemo(() => {
    const p = new URLSearchParams(queryParams);
    p.set("sort", "level");
    return p;
  }, [queryParams]);
  const nodesFetch = usePagedQuery<AuditNodesResponse>("/api/audit/nodes", queryParams, PAGE_SIZE, filters.sub === "nodes");
  const findingsFetch = usePagedQuery<AuditFindingsResponse>("/api/audit/findings", findingsQueryParams, PAGE_SIZE, filters.sub === "findings");
  const activeFetch = filters.sub === "nodes" ? nodesFetch : findingsFetch;
  const activeData = activeFetch.data;

  // Only the Nodes response carries color_totals (a node-level count) —
  // the chip row shows counts when available and just the label/chip
  // otherwise (e.g. while the Findings sub-view is active).
  const colorTotals: Record<string, number> =
    (nodesFetch.data && nodesFetch.data !== "unavailable" && nodesFetch.data.color_totals) || {};

  // The Advert-interval column header's own tooltip (2026-09-26 check-
  // fixes batch 1, fix 3) — quotes the SAME Settings-backed boundaries
  // this report's own traffic light was coloured with (nodes.py's
  // `advert_color_settings`), never a hard-coded 12/25/47, so a future
  // change to those Settings is reflected here automatically.
  const advertColorSettings = nodesFetch.data && nodesFetch.data !== "unavailable" ? nodesFetch.data.advert_color_settings : undefined;
  // Settings-coverage pass (part 4, 2026-09-27): same "quote the live
  // config, never a hard-coded number" rule as advertColorSettings above —
  // feeds clock_offset's own tooltip (see auditIssueTooltip, definitions.ts).
  const clockColorSettings = nodesFetch.data && nodesFetch.data !== "unavailable" ? nodesFetch.data.clock_color_settings : undefined;
  // "from N gaps over X days" (2026-09-26 check-fixes batch 1, fix 3) —
  // same lookback the colour column itself queried, never hard-coded.
  const advertLookbackDays = nodesFetch.data && nodesFetch.data !== "unavailable" ? nodesFetch.data.advert_lookback_days : undefined;

  // 1c ("NO-COLOUR BREAKDOWN", 2026-09-26 check-fixes batch 1) — sub-counts
  // of the "No colour" bucket by reason, straight from the API (never
  // hard-coded/re-derived client-side). Declared AFTER advertColorSettings
  // above, which its own label formatting reads.
  const noColourReasonTotals: Record<string, number> =
    (nodesFetch.data && nodesFetch.data !== "unavailable" && nodesFetch.data.no_colour_reason_totals) || {};
  const noColourBreakdownLine = (Object.keys(noColourReasonTotals) as AdvertNoColourReason[])
    .filter(isAdvertNoColourReason)
    .map((r) => `${advertNoColourReasonLabel(r, null, advertColorSettings?.neutral_ceiling_hours)}: ${noColourReasonTotals[r]}`)
    .join(" · ");
  const noColourBreakdownTitle = noColourBreakdownLine || "No nodes currently have no colour under the other active filters.";
  // 2026-09-26 check-fixes third pass, "WHY SO MANY REPEATERS AT EXACTLY
  // 3.0h" — see backend/amalgamesh/audit/detectors/d1_advert_rate.py's own
  // FIRMWARE_OLD_3H_DEFAULT_NOTE citation (firmware history + a 3-repeater
  // raw-data check) for why an identical 3.0h reading across many repeaters
  // is real firmware history, not an audit bug.
  const advertIntervalHeaderTitle = advertColorSettings
    ? `The observed gap between this node's own flood adverts (the mode of its inter-arrival times) — not the node's configured setting. ` +
      `Green ${advertColorSettings.green_from_hours}–${advertColorSettings.neutral_ceiling_hours} h, ` +
      `yellow ${advertColorSettings.yellow_from_hours}–${advertColorSettings.green_from_hours} h, ` +
      `red under ${advertColorSettings.yellow_from_hours} h — set in Settings. 3h was MeshCore's original factory default ` +
      `(repeater/room-server firmware v1.7.4 and earlier, pre-Aug-2025) — raised to 12h, then to 47h for new installs by ` +
      `PR #2608 (2026-05-23); a node still at 3h simply hasn't been reflashed since.`
    : "The observed gap between this node's own flood adverts — not the node's configured setting.";

  const erroredDetectors = useMemo(() => {
    if (!activeData || activeData === "unavailable" || !activeData.detector_status) return [];
    return Object.entries(activeData.detector_status)
      .filter(([, s]) => s.state === "error")
      .map(([id, s]) => ({ id, error: s.last_error }));
  }, [activeData]);

  const activeView = filters.view && Array.isArray(views) ? views.find((v) => String(v.id) === filters.view) ?? null : null;
  const filtersDifferFromActiveView = activeView ? canonicalize({ ...activeViewFilters, ...filtersToViewPayload(filters) }) !== canonicalize(activeViewFilters ?? {}) : false;

  function updateFilters(patch: Partial<FilterState>) {
    setFilters((f) => ({ ...f, ...patch }));
  }

  function toggleColor(color: AuditColor) {
    updateFilters({ color: filters.color.includes(color) ? filters.color.filter((c) => c !== color) : [...filters.color, color] });
  }

  function toggleInList<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  }

  function selectView(view: AuditSavedView) {
    setActiveViewFilters(view.filters);
    setFilters((f) => ({ ...applyViewFilters(f, view.filters), view: String(view.id) }));
    setQDraft(typeof view.filters.q === "string" ? view.filters.q : "");
  }

  async function refreshViews() {
    const res = await tryApiGet<AuditSavedViewsResponse>("/api/audit/views");
    setViews(res ? res.views : "unavailable");
    return res?.views ?? [];
  }

  async function saveViewAsNew() {
    const name = window.prompt("Name this view:");
    if (!name) return;
    try {
      const created = await apiPost<AuditSavedView>("/api/audit/views", { name, filters: { ...filtersToViewPayload(filters), protocol } });
      await refreshViews();
      setActiveViewFilters(created.filters);
      updateFilters({ view: String(created.id) });
    } catch (e) {
      window.alert(e instanceof ApiError ? e.message : "Failed to save view.");
    }
  }

  async function updateActiveView() {
    if (!activeView) return;
    try {
      const updated = await apiPut<AuditSavedView>(`/api/audit/views/${activeView.id}`, { filters: { ...filtersToViewPayload(filters), protocol } });
      await refreshViews();
      setActiveViewFilters(updated.filters);
    } catch (e) {
      window.alert(e instanceof ApiError ? e.message : "Failed to update view.");
    }
  }

  async function renameActiveView() {
    if (!activeView) return;
    const name = window.prompt("Rename view:", activeView.name);
    if (!name || name === activeView.name) return;
    try {
      await apiPut(`/api/audit/views/${activeView.id}`, { name });
      await refreshViews();
    } catch (e) {
      window.alert(e instanceof ApiError ? e.message : "Failed to rename view.");
    }
  }

  async function deleteActiveView() {
    if (!activeView) return;
    if (!window.confirm(`Delete the saved view "${activeView.name}"?`)) return;
    try {
      await apiDelete(`/api/audit/views/${activeView.id}`);
      await refreshViews();
      updateFilters({ view: null });
      setActiveViewFilters(null);
    } catch (e) {
      window.alert(e instanceof ApiError ? e.message : "Failed to delete view.");
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyFeedback("Copied!");
    } catch {
      setCopyFeedback("Could not copy — copy the address bar instead.");
    }
    setTimeout(() => setCopyFeedback(null), 2000);
  }

  async function exportViews(currentOnly: boolean) {
    const path = currentOnly && activeView ? `/api/audit/views/export?id=${activeView.id}` : "/api/audit/views/export";
    try {
      const res = await fetch(path, { credentials: "include", headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`export responded ${res.status}`);
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = /filename="([^"]+)"/.exec(disposition);
      const filename = match ? match[1] : "amalgamesh-audit-views.json";
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(`Export failed: ${(e as Error).message}`);
    }
  }

  async function handleImportFile(file: File) {
    setImportError(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      await apiPost("/api/audit/views/import", parsed);
      await refreshViews();
    } catch (e) {
      setImportError(e instanceof Error ? e.message : "Import failed — file was not valid JSON in the expected shape.");
    }
  }

  // Protocol-scoped (task's own requirement): a view saved with a
  // `protocol` in its filters is only listed on that side; a view with no
  // `protocol` at all (every seeded builtin, and any view saved before
  // this task) is protocol-agnostic and listed on both.
  const viewMatchesProtocol = (v: AuditSavedView) => {
    const p = v.filters?.protocol;
    return !p || p === protocol;
  };
  const builtinViews = Array.isArray(views) ? views.filter((v) => v.builtin && viewMatchesProtocol(v)) : [];
  const customViews = Array.isArray(views) ? views.filter((v) => !v.builtin && viewMatchesProtocol(v)) : [];

  // The dropdown must reflect ANY filter state that happens to exactly
  // match a saved view's filters, not just one reached via an explicit
  // selectView() call or a `?view=` link — this is what makes a fresh
  // load with no URL params (color defaults to DEFAULT_AUDIT_COLORS,
  // byte-for-byte the seeded "Default" builtin's own filters, migration
  // 0035) show "Default" instead of a misleading "No view selected". Only
  // consulted when `filters.view` itself is unset; an explicit selection
  // always wins.
  const matchedView = useMemo(() => {
    if (filters.view || !Array.isArray(views) || views.length === 0) return null;
    const payload = canonicalize(filtersToViewPayload(filters));
    return views.find((v) => canonicalize(v.filters) === payload) ?? null;
  }, [filters, views]);
  const viewSelectValue = filters.view ?? (matchedView ? String(matchedView.id) : "");
  // 2026-09-26 (check-fixes batch 1, fix 4): `matchedView` above can only
  // ever match once `views` has actually loaded from GET /api/audit/views
  // — on a genuinely fresh arrival, `views` starts `null` (see its own
  // useState above), so for however long that request is in flight the
  // fallback "" option below rendered the literal string "Custom
  // (unsaved)" even though the filters ARE exactly the seeded "Default"
  // builtin's own payload (byte-for-byte, per `matchedView`'s own
  // comment) — a real race, not a screenshot artifact (confirmed:
  // simplify-v2's own screenshot script opens `/#/audit` with no URL
  // params at all, identically for both the desktop and phone captures;
  // the desktop capture's node-list rows simply finished loading, and its
  // screenshot fired, before the separate views request resolved). Fixed
  // by checking the filters against the Default payload directly,
  // independent of whether `views` has loaded yet — this is exactly the
  // same comparison `matchedView` makes, just evaluated against a literal
  // rather than a fetched list, so it's correct from the very first
  // render instead of only after a network round-trip.
  const looksLikeDefaultFilters =
    !filters.view && canonicalize(filtersToViewPayload(filters)) === canonicalize({ color: DEFAULT_AUDIT_COLORS });

  // Drives the "More filters" disclosure's own open/closed default — open
  // whenever anything it holds is actually active (a shared link, a
  // restored saved view, or a value the user set earlier), collapsed
  // otherwise — collapsed by default and remembered in the URL only
  // when used. "No colour" no longer counts (2026-09-26 check-fixes
  // batch 1, fix 4 moved it into the always-visible chip row, next to
  // Red/Yellow/Green) — it isn't a "More filters" control any more.
  const moreFiltersActive =
    filters.verdict.length > 0 ||
    !!filters.observer ||
    !!filters.first_seen_from ||
    !!filters.last_seen_to ||
    filters.role.length > 0 ||
    Object.keys(filters.evidence).length > 0;

  const detectorValue = filters.detector[0] ?? "";

  return (
    <section className="page-section">
      {erroredDetectors.length > 0 && (
        <div className="audit-detector-error">
          This check failed to run: {erroredDetectors.map((d) => `${detectorLabel(d.id)} (${d.error || "unknown error"})`).join("; ")}
        </div>
      )}

      {/* --- Views bar (compact row above the filter row) --- */}
      <div className="node-list-toolbar">
        <select
          value={viewSelectValue}
          onChange={(e) => {
            const id = e.target.value;
            if (!id) {
              updateFilters({ view: null });
              setActiveViewFilters(null);
              return;
            }
            const v = [...builtinViews, ...customViews].find((x) => String(x.id) === id);
            if (v) selectView(v);
          }}
        >
          <option value="">{looksLikeDefaultFilters ? "Default" : "Custom (unsaved)"}</option>
          {builtinViews.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
          {customViews.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
        <button type="button" className="settings-record-add-btn" onClick={saveViewAsNew}>
          Save view
        </button>
        {activeView && !activeView.builtin && filtersDifferFromActiveView && (
          <button type="button" className="settings-record-add-btn" onClick={updateActiveView}>
            Update view
          </button>
        )}
        {activeView && !activeView.builtin && (
          <button type="button" className="settings-record-add-btn" onClick={renameActiveView}>
            Rename
          </button>
        )}
        {activeView && !activeView.builtin && (
          <button type="button" className="settings-record-remove-btn" onClick={deleteActiveView}>
            Delete
          </button>
        )}
        <button type="button" className="settings-record-add-btn" onClick={copyLink}>
          Copy link
        </button>
        <button type="button" className="settings-record-add-btn" onClick={() => exportViews(false)}>
          Export all
        </button>
        {activeView && (
          <button type="button" className="settings-record-add-btn" onClick={() => exportViews(true)}>
            Export this view
          </button>
        )}
        <button type="button" className="settings-record-add-btn" onClick={() => fileInputRef.current?.click()}>
          Import
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleImportFile(file);
            e.target.value = "";
          }}
        />
        {copyFeedback && <span className="settings-field-desc">{copyFeedback}</span>}
        {importError && <span className="settings-field-desc">{importError}</span>}
      </div>

      {/* --- The one filter row (2026-09-25, simplified for clarity):
          search, the
          red/yellow/green colour chips, a single Check dropdown, a window
          dropdown. Everything else lives under "More filters" below. --- */}
      <div className="node-list-toolbar">
        <input
          className="node-list-search"
          type="text"
          placeholder="Search node name or ID…"
          value={qDraft}
          onChange={(e) => setQDraft(e.target.value)}
        />
        <div className="theme-toggle" role="group" aria-label="Colour">
          {PRIMARY_COLOR_CHIPS.map((c) => {
            const info = AUDIT_COLOR_INFO[c];
            const count = colorTotals[c];
            return (
              <button key={c} type="button" data-active={filters.color.includes(c)} onClick={() => toggleColor(c)}>
                {info.color && (
                  <span
                    className="legend-swatch"
                    style={{ background: colorFor(info.color, effectiveTheme), display: "inline-block", marginRight: 4 }}
                  />
                )}
                {info.label}
                {typeof count === "number" ? ` (${count})` : " (…)"}
              </button>
            );
          })}
          {/* "No colour" (2026-09-26 check-fixes batch 1, fix 4, "NO-COLOUR
              COUNT UP FRONT") — moved out of "More filters" into the
              always-visible row, next to Red/Yellow/Green (default still
              red+yellow, unchanged — DEFAULT_AUDIT_COLORS). Covers four
              distinct reasons (see AdvertIntervalCell's own per-row
              breakdown for each node): not enough adverts yet, over the
              neutral ceiling, no confirmed timer (companion/observer), or
              no adverts heard at all. */}
          <button
            type="button"
            data-active={filters.color.includes(NO_COLOUR_CHIP)}
            onClick={() => toggleColor(NO_COLOUR_CHIP)}
            title={`Nodes with no D1 colour, by reason — ${noColourBreakdownTitle}`}
          >
            {AUDIT_COLOR_INFO[NO_COLOUR_CHIP].label}
            {typeof colorTotals[NO_COLOUR_CHIP] === "number" ? ` (${colorTotals[NO_COLOUR_CHIP]})` : " (…)"}
          </button>
        </div>
        {/* 1c ("NO-COLOUR BREAKDOWN"): a small grey line under the chip row,
            shown only while "No colour" is one of the active chips — the
            SAME per-reason counts the chip's own tooltip carries, from the
            API (no_colour_reason_totals), never re-derived client-side. */}
        {filters.color.includes(NO_COLOUR_CHIP) && noColourBreakdownLine && (
          <span className="settings-field-desc" style={{ flexBasis: "100%" }}>
            No colour breakdown — {noColourBreakdownLine}
          </span>
        )}
        <select value={detectorValue} onChange={(e) => updateFilters({ detector: e.target.value ? [e.target.value] : [] })}>
          <option value="">All checks</option>
          {protocolDetectors.map((d) => (
            <option key={d.id} value={d.id}>
              {detectorLabel(d.id, d.label)}
            </option>
          ))}
        </select>
        {/* 1a ("TWO WINDOWS", 2026-09-26 check-fixes batch 1): this menu
            drives the findings window for every OTHER check — it has never
            applied to the Advert-interval column, which always uses D1's
            own fixed look-back (advert_report_window/advert_lookback_
            hours), independent of this control. 2026-09-26 check-fixes
            third pass, fix 4: label now says what it covers ("Window for
            other checks:"), and the tooltip names those other checks by
            their real UI labels (otherChecksNames, above) instead of a
            hard-coded, driftable "D2, D3, D4, D5, D7, D8...". */}
        <span className="settings-field-desc" title={otherChecksTooltip}>
          Window for other checks:
        </span>
        <select value={takenAt ? "7d" : filters.window} disabled={!!takenAt} onChange={(e) => updateFilters({ window: e.target.value as AuditWindow })}>
          {AUDIT_WINDOW_ORDER.map((w) => (
            <option key={w} value={w}>
              {w}
            </option>
          ))}
        </select>
        <AuditHistorySelect />
      </div>

      {/* --- More filters (collapsed by default; open when it holds an
          active value — see moreFiltersActive above) --- */}
      <details className="settings-record-optional" open={moreFiltersActive}>
        <summary>More filters</summary>

        <div className="node-list-toolbar">
          <span className="settings-field-desc">Heard by</span>
          <select value={filters.observer} onChange={(e) => updateFilters({ observer: e.target.value })}>
            <option value="">Any</option>
            {observers.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label || o.id}
              </option>
            ))}
          </select>
        </div>

        <div className="node-list-toolbar">
          <span className="settings-field-desc" title="Proven/Likely/Not enough data — see each option's own tooltip for exactly what it means.">
            How sure:
          </span>
          {AUDIT_VERDICT_ORDER.map((v) => (
            <label key={v} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }} title={AUDIT_VERDICT_TOOLTIP[v]}>
              <input type="checkbox" checked={filters.verdict.includes(v)} onChange={() => updateFilters({ verdict: toggleInList(filters.verdict, v) })} />
              {auditVerdictInfo(v).label}
            </label>
          ))}
          <span className="settings-field-desc" style={{ marginLeft: 12 }}>
            First seen from
          </span>
          <input type="date" value={filters.first_seen_from} onChange={(e) => updateFilters({ first_seen_from: e.target.value })} />
          <span className="settings-field-desc">Last seen to</span>
          <input type="date" value={filters.last_seen_to} onChange={(e) => updateFilters({ last_seen_to: e.target.value })} />
        </div>

        <div className="node-list-toolbar">
          <span className="settings-field-desc">Role:</span>
          {roleOrder.map((r) => (
            <label key={r} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
              <input type="checkbox" checked={filters.role.includes(r)} onChange={() => updateFilters({ role: toggleInList(filters.role, r) })} />
              {auditRoleLabel(r)}
            </label>
          ))}
        </div>
        <div className="node-list-toolbar">
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }} title="Coming soon">
            <input type="checkbox" disabled /> Tags — Coming soon
          </label>
        </div>

        {relevantEvidenceFields.length > 0 && (
          <div className="node-list-toolbar">
            {relevantEvidenceFields.map((field) => (
              <div key={field} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span className="settings-field-desc" title={AUDIT_EVIDENCE_FIELD_TOOLTIP[field]}>
                  {AUDIT_EVIDENCE_FIELD_LABEL[field]}
                </span>
                <div style={{ display: "flex", gap: 4 }}>
                  <input
                    type="number"
                    placeholder="min"
                    style={{ width: 70 }}
                    value={filters.evidence[`ev_${field}_min`] ?? ""}
                    onChange={(e) => updateFilters({ evidence: { ...filters.evidence, [`ev_${field}_min`]: e.target.value } })}
                  />
                  <input
                    type="number"
                    placeholder="max"
                    style={{ width: 70 }}
                    value={filters.evidence[`ev_${field}_max`] ?? ""}
                    onChange={(e) => updateFilters({ evidence: { ...filters.evidence, [`ev_${field}_max`]: e.target.value } })}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </details>

      {/* --- Sub-view toggle --- */}
      <div className="theme-toggle" role="group" aria-label="Sub-view" style={{ margin: "12px 0" }}>
        <button data-active={filters.sub === "nodes"} onClick={() => updateFilters({ sub: "nodes" })}>
          Nodes
        </button>
        <button data-active={filters.sub === "findings"} onClick={() => updateFilters({ sub: "findings" })}>
          Findings
        </button>
      </div>

      {activeData === null && <div className="node-detail-note">Loading…</div>}
      {activeData === "unavailable" && <div className="node-detail-note">Not available from this backend right now.</div>}

      {filters.sub === "nodes" && activeData && activeData !== "unavailable" && (
        <>
          <div className="node-list-table-wrap behaviour-nodes-table-wrap">
            <table className="node-list-table behaviour-nodes-table">
              <thead>
                <tr>
                  <th>Node</th>
                  <th>Role</th>
                  <th title={advertIntervalHeaderTitle}>
                    Advert interval (measured)
                    {/* 1a ("TWO WINDOWS"): the "Other checks:" window menu
                        above never narrows this column — it always measures
                        over its own fixed look-back, so the same fact from
                        the window's own comment is restated here, in whole
                        days, right where a reader is looking. */}
                    {/* 2026-09-26 check-fixes third pass, fix 3 ("ALWAYS
                        OVER THE LAST 5.9 DAYS"): advertLookbackDays now
                        arrives pre-rounded to a whole day from the backend
                        (nodes.py) — no decimal here to round again. */}
                    {advertLookbackDays != null && (
                      <div className="settings-field-desc" style={{ fontWeight: "normal" }}>
                        measured over the last {advertLookbackDays} day{advertLookbackDays === 1 ? "" : "s"}
                      </div>
                    )}
                  </th>
                  <th>Issues</th>
                </tr>
              </thead>
              <tbody>
                {(nodesFetch.data as AuditNodesResponse | "unavailable" | null) &&
                  Array.isArray((activeData as AuditNodesResponse).nodes) &&
                  (activeData as AuditNodesResponse).nodes.map((n) => (
                    <NodeRow
                      key={`${n.protocol}:${n.native_id}`}
                      node={n}
                      queryParams={queryParams}
                      detectors={detectors}
                      advertLookbackDays={advertLookbackDays}
                      advertNeutralCeilingHours={advertColorSettings?.neutral_ceiling_hours}
                      clockColorSettings={clockColorSettings}
                    />
                  ))}
                {(activeData as AuditNodesResponse).nodes.length === 0 && (
                  <tr>
                    <td colSpan={4} className="empty-state">
                      No nodes match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <PagingBar
            rangeStart={(activeData as AuditNodesResponse).nodes.length === 0 ? 0 : (nodesFetch.page - 1) * PAGE_SIZE + 1}
            rangeEnd={(nodesFetch.page - 1) * PAGE_SIZE + (activeData as AuditNodesResponse).nodes.length}
            total={(activeData as AuditNodesResponse).total}
            hasPrev={nodesFetch.hasPrev}
            hasNext={nodesFetch.hasNext}
            onPrev={nodesFetch.prev}
            onNext={nodesFetch.next}
          />
        </>
      )}

      {filters.sub === "findings" && activeData && activeData !== "unavailable" && (
        <>
          <FindingsSubTable
            findings={(activeData as AuditFindingsResponse).findings}
            showEmptyMessage="No findings match the current filters."
            detectors={detectors}
          />
          <PagingBar
            rangeStart={(activeData as AuditFindingsResponse).findings.length === 0 ? 0 : (findingsFetch.page - 1) * PAGE_SIZE + 1}
            rangeEnd={(findingsFetch.page - 1) * PAGE_SIZE + (activeData as AuditFindingsResponse).findings.length}
            total={(activeData as AuditFindingsResponse).total}
            hasPrev={findingsFetch.hasPrev}
            hasNext={findingsFetch.hasNext}
            onPrev={findingsFetch.prev}
            onNext={findingsFetch.next}
          />
        </>
      )}
    </section>
  );
}
